// src/components/layout/RightPanel/ProjectPortal.jsx
import React, { forwardRef, useImperativeHandle, useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Copy, Download, Code,
  Type, Minus, Plus, RefreshCw, AlertTriangle, FileText, Save, Loader2
} from 'lucide-react';
import { usePanelManager, CONTENT_TYPES } from '@/managers/PanelManager';
import { workspaceManager } from '@/managers/workspaceManager';
import { apiManager } from '@/components/layout/RightPanel/shelbulaworkspaces/apiManager';
import UniversalEditor from './UniversalEditor';

/* ========================
 * FontSizePopover - Compact font size control
 * ======================== */
const FontSizePopover = ({ fontSize, onFontSizeChange, isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div
      className="absolute right-0 top-full mt-1 bg-surface border border-border-color rounded-md shadow-lg p-2 z-[9999] font-size-popover-el"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-2 p-1">
        <button
          onClick={() => onFontSizeChange(Math.max(10, fontSize - 1))}
          className="p-1 hover:bg-surface rounded-md transition-colors text-on-surface-dim hover:text-on-surface"
        >
          <Minus className="w-3.5 h-3.5" />
        </button>
        <span className="text-xs text-on-surface-dim font-mono min-w-[28px] text-center">
          {fontSize}px
        </span>
        <button
          onClick={() => onFontSizeChange(Math.min(24, fontSize + 1))}
          className="p-1 hover:bg-surface rounded-md transition-colors text-on-surface-dim hover:text-on-surface"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => onFontSizeChange(14)}
          className="p-1 hover:bg-surface rounded-md transition-colors text-on-surface-dim hover:text-on-surface ml-1"
          title="Reset to default size"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

/* ========================
 * ProjectPortal - Universal content portal
 * Manages different content types and editor states, including streaming
 * ======================== */
const ProjectPortal = forwardRef(({ isOpen, onClose, embedded = false, forcedMode = null, hideCloseButton = false, workspaceId = null }, ref) => {
  const { getPortalContent, clearPortalContent, showInPortal } = usePanelManager();
  const [copySuccess, setCopySuccess] = useState(false);
  const [fontSize, setFontSize] = useState(() => {
    const savedSize = localStorage.getItem('shelbula_codepanel_fontsize');
    return savedSize ? parseInt(savedSize, 10) : 14;
  });

  const [isFontSizePopoverOpen, setIsFontSizePopoverOpen] = useState(false);

  const [localContentByMode, setLocalContentByMode] = useState({
    [CONTENT_TYPES.CODE]: '',
    [CONTENT_TYPES.DOCUMENT]: ''
  });
  const [editorsReadyForDisplay, setEditorsReadyForDisplay] = useState(false);

  const codeEditorRef = useRef(null);
  const fontSizeButtonRef = useRef(null);

  const portalContentObject = getPortalContent();
  const hasPortalContent = !!portalContentObject;
  const portalContentType = portalContentObject?.type;
  const portalMetadata = portalContentObject?.metadata;
  const isStreamingToPortal = portalContentObject?.isStreaming || false;

  const [userDrivenMode, setUserDrivenMode] = useState(CONTENT_TYPES.CODE);

  const defaultFileName = forcedMode === CONTENT_TYPES.DOCUMENT ? 'New Document.md' : 'New Code File.js';
  const [fileName, setFileName] = useState(defaultFileName);
  const [isSaving, setIsSaving] = useState(false);
  const [saveOk, setSaveOk] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const resolveWritableWorkspaceId = useCallback(() => {
    if (workspaceId) {
      return workspaceId;
    }

    const workspaceState = workspaceManager.getState();
    return workspaceState.activeWorkspaceDetail?.id
      || workspaceState.workspaces?.find((workspace) => workspace.isDefault)?.id
      || workspaceState.activeWorkspaceId
      || null;
  }, [workspaceId]);

  const activeMode = useMemo(() => {
    if (forcedMode) {
      return forcedMode;
    }

    return portalContentType || userDrivenMode;
  }, [forcedMode, portalContentType, userDrivenMode]);

  useEffect(() => {
    localStorage.setItem('shelbula_codepanel_fontsize', fontSize.toString());
  }, [fontSize]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isFontSizePopoverOpen &&
          fontSizeButtonRef.current && 
          !fontSizeButtonRef.current.contains(event.target)) {
        const popoverElement = document.querySelector('.font-size-popover-el');
        if (popoverElement && !popoverElement.contains(event.target)) {
            setIsFontSizePopoverOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isFontSizePopoverOpen]);

  useEffect(() => {
    if (!isOpen) {
      setEditorsReadyForDisplay(false);
      if (!hasPortalContent && !forcedMode) setUserDrivenMode(CONTENT_TYPES.CODE);
    }
  }, [isOpen, hasPortalContent, forcedMode]);

  useEffect(() => {
    if (!isOpen) return;
    if (hasPortalContent || userDrivenMode !== portalContentType) {
      setEditorsReadyForDisplay(true);
    }
  }, [isOpen, hasPortalContent, userDrivenMode, portalContentType]);

  useEffect(() => {
    if (!isOpen || !editorsReadyForDisplay || isStreamingToPortal) return;
    const timeoutId = setTimeout(() => {
      if ((activeMode === CONTENT_TYPES.CODE || activeMode === CONTENT_TYPES.MERMAID) && codeEditorRef.current) {
        codeEditorRef.current.focus?.();
      }
    }, 200);
    return () => clearTimeout(timeoutId);
  }, [isOpen, editorsReadyForDisplay, activeMode, isStreamingToPortal]);

  const setContentImperative = useCallback((type, text, meta = {}) => {
    const portalId = meta.portalId || `imperative-${Date.now()}`;
    showInPortal({ type, content: text, metadata: { ...meta, portalId, contentType: type } });
  }, [showInPortal]);

  useImperativeHandle(ref, () => ({ setContent: setContentImperative }), [setContentImperative]);

  const handleCodeChange = useCallback((newContent) => {
    const isCodeOrMermaid = portalContentType === CONTENT_TYPES.CODE || portalContentType === CONTENT_TYPES.MERMAID;
    if (hasPortalContent && isCodeOrMermaid && !isStreamingToPortal) {
      showInPortal({ ...portalContentObject, content: newContent, isStreaming: false });
    } else if (hasPortalContent && portalContentType === CONTENT_TYPES.DOCUMENT && !isStreamingToPortal) {
      showInPortal({ ...portalContentObject, content: newContent, isStreaming: false });
    } else if (!hasPortalContent) {
      setLocalContentByMode(prev => ({
        ...prev,
        [activeMode]: newContent,
      }));
    }
  }, [activeMode, hasPortalContent, portalContentType, isStreamingToPortal, showInPortal, portalContentObject]);

  const handleCopy = useCallback(async () => {
    try {
      let contentToCopy = hasPortalContent 
          ? (isStreamingToPortal ? portalContentObject?.accumulatedContent : portalContentObject?.content)
          : localContentByMode[activeMode] || '';
      if (typeof contentToCopy !== 'string') return;
      await navigator.clipboard.writeText(contentToCopy);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) { console.error('Copy failed:', error); }
  }, [activeMode, hasPortalContent, portalContentObject, isStreamingToPortal, localContentByMode]);

  const handleDownload = useCallback(() => {
    try {
      let contentToDownload;
      let metadataForFilename = {};
      if (hasPortalContent) {
        contentToDownload = isStreamingToPortal ? portalContentObject?.accumulatedContent : portalContentObject?.content;
        metadataForFilename = portalMetadata || {};
      } else {
        contentToDownload = localContentByMode[activeMode] || '';
        metadataForFilename = { language: activeMode === CONTENT_TYPES.DOCUMENT ? 'markdown' : 'plaintext' };
      }
      if (typeof contentToDownload !== 'string') return;
      const filename = getFilename(portalContentType || activeMode, metadataForFilename);
      const blob = new Blob([contentToDownload], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
    } catch (error) { console.error('Download failed:', error); }
  }, [activeMode, hasPortalContent, portalContentObject, isStreamingToPortal, portalContentType, portalMetadata, localContentByMode]);

  const handleClose = useCallback(() => { onClose?.(); }, [onClose]);
  const handleFontSizeChange = useCallback((newSize) => { setFontSize(newSize); }, []);
  const toggleFontSizePopover = useCallback(() => setIsFontSizePopoverOpen(prev => !prev), []);

  const contentForEditor = useMemo(() => {
    if (hasPortalContent) {
      return isStreamingToPortal ? portalContentObject?.accumulatedContent : portalContentObject?.content;
    }
    return localContentByMode[activeMode] || '';
  }, [hasPortalContent, isStreamingToPortal, portalContentObject, activeMode, localContentByMode]);

  const handleSaveToWorkspace = useCallback(async () => {
    if (isSaving) return;
    const content = contentForEditor;
    if (!content?.trim()) return;
    const resolvedWorkspaceId = resolveWritableWorkspaceId();
    if (!resolvedWorkspaceId) {
      setSaveError('Workspace is still loading. Try again in a moment.');
      setTimeout(() => setSaveError(null), 4000);
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      await apiManager.saveFileContent(resolvedWorkspaceId, fileName, content, `[Human Action] Created ${fileName}`);
      setLocalContentByMode(prev => ({ ...prev, [activeMode]: '' }));
      setFileName(forcedMode === CONTENT_TYPES.DOCUMENT ? 'New Document.md' : 'New Code File.js');
      window.dispatchEvent(new CustomEvent('workspace-refresh-required'));
      setSaveOk(true);
      setTimeout(() => setSaveOk(false), 2500);
    } catch (err) {
      console.error('[ProjectPortal] Save to workspace failed:', err);
      setSaveError(err?.message || 'Save failed.');
      setTimeout(() => setSaveError(null), 4000);
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, contentForEditor, fileName, activeMode, forcedMode, resolveWritableWorkspaceId]);

  const codeEditorLanguageToUse = useMemo(() => {
    if (activeMode === CONTENT_TYPES.MERMAID) return "mermaid";
    if (activeMode === CONTENT_TYPES.DOCUMENT) return "markdown";
    if (hasPortalContent && portalMetadata?.language) return portalMetadata.language;
    return "javascript"; 
  }, [activeMode, hasPortalContent, portalMetadata]);

  const metadataForCurrentEditor = useMemo(() => {
    return hasPortalContent ? portalMetadata : { language: codeEditorLanguageToUse };
  }, [hasPortalContent, portalMetadata, codeEditorLanguageToUse]);

  const isEditorEffectivelyReadOnly = useMemo(() => {
    return isStreamingToPortal;
  }, [isStreamingToPortal]);

  const TitleIcon = activeMode === CONTENT_TYPES.DOCUMENT ? FileText : Code;
  const hasLocalContent = !!(contentForEditor?.trim());

  const shellContent = (
    <>
      <div className="flex items-center justify-between h-[50px] px-3 border-b border-border-color bg-surface flex-shrink-0">
        <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <div className="flex rounded-md overflow-hidden border border-border-color flex-shrink-0">
            <div className="px-3 py-1 text-xs bg-primary text-white flex items-center gap-1.5">
              <TitleIcon className="w-3.5 h-3.5" />
              <span>{activeMode === CONTENT_TYPES.DOCUMENT ? 'Document' : 'Code'}</span>
            </div>
          </div>
          {forcedMode && (
            <input
              type="text"
              value={fileName}
              onChange={e => setFileName(e.target.value)}
              className="text-xs text-on-surface bg-transparent outline-none border-b border-border-color focus:border-primary min-w-0 w-36 py-0.5"
              aria-label="File name"
            />
          )}
        </div>
          <div className="relative">
            <button
              ref={fontSizeButtonRef}
              onClick={toggleFontSizePopover}
              className="p-1.5 hover:bg-surface rounded-md transition-colors text-on-surface-dim hover:text-on-surface"
              aria-label="Font size settings"
            >
              <Type className="w-4 h-4" />
            </button>
            <FontSizePopover
              fontSize={fontSize}
              onFontSizeChange={handleFontSizeChange}
              isOpen={isFontSizePopoverOpen}
              onClose={() => setIsFontSizePopoverOpen(false)}
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleCopy}
            disabled={!editorsReadyForDisplay && !isStreamingToPortal}
            className="flex items-center gap-1.5 px-3 py-1.5 hover:bg-surface rounded-md transition-colors text-on-surface-dim hover:text-on-surface"
            aria-label="Copy content"
          >
            <Copy className="w-4 h-4" />
            <span className="text-xs font-medium">COPY</span>
          </button>
          <button
            onClick={handleDownload}
            disabled={!editorsReadyForDisplay && !isStreamingToPortal}
            className="p-1.5 hover:bg-surface rounded-md transition-colors text-on-surface-dim hover:text-on-surface"
            aria-label="Download content"
          >
            <Download className="w-4 h-4" />
          </button>
          {forcedMode && hasLocalContent && !isStreamingToPortal && (
            <button
              onClick={handleSaveToWorkspace}
              disabled={isSaving}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-success/20 hover:bg-success/30 text-success rounded-md transition-colors text-xs font-medium disabled:opacity-50"
              aria-label="Save to workspace"
            >
              {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              <span>SAVE</span>
            </button>
          )}
          {!hideCloseButton && (
            <button
              onClick={handleClose}
              className="p-1.5 hover:bg-surface rounded-md transition-colors ml-1 text-on-surface-dim hover:text-on-surface"
              aria-label="Close portal"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <AnimatePresence>
        {copySuccess && (
          <motion.div 
            key="copy-success-toast"
            initial={{ opacity: 0, y: -10 }} 
            animate={{ opacity: 1, y: 0 }} 
            exit={{ opacity: 0, y: -10 }} 
            className="absolute top-14 right-4 bg-primary text-white px-3 py-1 rounded-md text-sm z-10"
          >
            Copied!
          </motion.div>
        )}
        {saveOk && (
          <motion.div
            key="save-success-toast"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-14 right-4 bg-success text-white px-3 py-1 rounded-md text-sm z-10"
          >
            Saved to workspace!
          </motion.div>
        )}
        {saveError && (
          <motion.div
            key="save-error-toast"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-14 right-4 bg-error text-white px-3 py-1 rounded-md text-sm z-10"
          >
            {saveError}
          </motion.div>
        )}
      </AnimatePresence>

      {isStreamingToPortal && (
        <div className="absolute top-[50px] left-0 right-0 p-2 bg-primary/10 border-b border-primary/30 z-10">
            <div className="flex items-center justify-center gap-2 text-xs text-primary">
                <AlertTriangle className="w-3.5 h-3.5 animate-pulse" />
                <span>Streaming content... Editor is read-only.</span>
            </div>
        </div>
      )}

      {editorsReadyForDisplay && (
        <div className={`flex-1 overflow-hidden relative ${isStreamingToPortal ? 'pt-[36px]' : ''}`}>
          {(activeMode === CONTENT_TYPES.CODE || activeMode === CONTENT_TYPES.MERMAID || activeMode === CONTENT_TYPES.DOCUMENT) && (
            <UniversalEditor
              key={`${activeMode}-editor-${portalMetadata?.portalId || forcedMode || 'local'}`}
              ref={codeEditorRef}
              contentString={String(contentForEditor || '')}
              language={codeEditorLanguageToUse}
              contentType={activeMode}
              metadata={metadataForCurrentEditor}
              onContentChange={handleCodeChange}
              fontSize={fontSize}
              readOnly={isEditorEffectivelyReadOnly}
            />
          )}
        </div>
      )}
    </>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="portal-content"
          initial={{ opacity: 0, x: '100%' }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: '100%' }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className={`h-full bg-background-dark ${embedded ? '' : 'border-l border-border-color shadow-2xl'} flex flex-col w-full relative`}
          onAnimationComplete={() => { 
            if (isOpen && (hasPortalContent || userDrivenMode !== portalContentType)) {
              setEditorsReadyForDisplay(true); 
            }
          }}
        >
          {shellContent}
        </motion.div>
      )}
    </AnimatePresence>
  );
});

ProjectPortal.displayName = 'ProjectPortal';

const getFilename = (contentType, metadata) => {
  const timestamp = new Date().toISOString().slice(0, 10);
  const safeLangOrType = (metadata?.language || contentType || 'file').replace(/[^a-zA-Z0-9]/g, '');
  const extensions = {
    javascript: 'js', typescript: 'ts', jsx: 'jsx', tsx: 'tsx',
    python: 'py', html: 'html', css: 'css', json: 'json',
    markdown: 'md', sql: 'sql', xml: 'xml', java: 'java', rust: 'rs',
    mermaid: 'mmd', plaintext: 'txt'
  };
  const ext = extensions[metadata?.language?.toLowerCase()?.trim()] || 'txt';
  
  switch (contentType) {
    case CONTENT_TYPES.CODE: return `code-${safeLangOrType}-${timestamp}.${ext}`;
    case CONTENT_TYPES.MERMAID: return `diagram-${safeLangOrType}-${timestamp}.mmd`;
    case CONTENT_TYPES.DOCUMENT: return `document-${safeLangOrType}-${timestamp}.md`;
    default: return `content-${safeLangOrType}-${timestamp}.txt`;
  }
};

export default ProjectPortal;